/**
 * AnimatedCollapse — 通用的 open/close 高度过渡动画容器。
 *
 * 展开：height 0 → scrollHeight → auto（移除固定高度，支持内容动态变化）
 * 收起：height scrollHeight → 0（使用 rAF 保证浏览器能记录起点）
 *
 * 被 ToolCallItem、ThinkingBlockView 等多处复用。
 */

import { useState, useEffect, useRef } from 'react'

export function AnimatedCollapse({ open, children }: { open: boolean; children: React.ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | undefined>(open ? undefined : 0)
  const [overflow, setOverflow] = useState<'hidden' | 'visible'>(open ? 'visible' : 'hidden')
  const firstRender = useRef(true)

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      if (open) {
        setHeight(undefined)
        setOverflow('visible')
      }
      return
    }

    const el = contentRef.current
    if (!el) return

    if (open) {
      const h = el.scrollHeight
      setHeight(0)
      setOverflow('hidden')
      // force reflow
      el.offsetHeight
      setHeight(h)
      const timer = setTimeout(() => {
        setHeight(undefined)
        setOverflow('visible')
      }, 250)
      return () => clearTimeout(timer)
    } else {
      // 先设置起点高度
      setHeight(el.scrollHeight)
      setOverflow('hidden')
      // 下一帧过渡到 0，避免在 React 渲染中调用 flushSync
      const raf = requestAnimationFrame(() => {
        el.offsetHeight // reflow，确保浏览器记录了起点
        setHeight(0)
      })
      return () => cancelAnimationFrame(raf)
    }
  }, [open])

  return (
    <div
      ref={contentRef}
      style={{
        height: height === undefined ? 'auto' : height,
        overflow,
        transition: 'height 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease',
        opacity: open ? 1 : 0,
      }}
    >
      {children}
    </div>
  )
}
